import type { StrapiEventListPage } from '~/composables/types/strapiEventListPage'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import { strapiEventListPageGetters } from '~/utils/strapiEventListPageGetters'

export const useEventListPageLocaleSwitcher = (
  eventListPageData: StrapiEventListPage | null | undefined
) => {
  const buildLocaleUrl = (targetLocale: string): string | null => {
    if (!eventListPageData) return null

    const normalizedTargetLocale = targetLocale.toLowerCase()
    const currentLocale = eventListPageData.locale?.toLowerCase()

    if (currentLocale === normalizedTargetLocale) return null

    const localizations =
      strapiEventListPageGetters.getLocalizations(eventListPageData)

    if (!localizations?.length) return null

    const targetLocalization = localizations.find(
      (loc) => loc.locale?.toLowerCase() === normalizedTargetLocale
    )

    if (!targetLocalization) return null

    const localePrefix = targetLocale.toLowerCase()
    const pageType = targetLocalization.type

    switch (pageType) {
      case EventListPageType.CITY:
        return `/${localePrefix}/${targetLocalization.citySlug}/`

      case EventListPageType.CATEGORY:
        return `/${localePrefix}/c/${targetLocalization.categorySlug}/`

      case EventListPageType.CITY_CATEGORY:
        return `/${localePrefix}/${targetLocalization.citySlug}/${targetLocalization.categorySlug}/`

      case EventListPageType.TEAMEVENT: {
        const fullSlug = targetLocalization.slug || ''

        // Extract city from slug
        // Slug format: "teambuilding-frankfurt-am-main" or "teamevent-frankfurt-am-main"
        // We need to remove the prefix (teamevent/teambuilding) and the locale suffix (_de-de/_en-us)
        let citySlug = fullSlug

        // Remove locale suffix if present (e.g., "_de-de" or "_en-us")
        if (citySlug.includes('_')) {
          citySlug = citySlug.split('_')[0]
        }

        // Remove prefix (teamevent- or teambuilding-)
        if (citySlug.startsWith('teamevent-')) {
          citySlug = citySlug.substring('teamevent-'.length)
        } else if (citySlug.startsWith('teambuilding-')) {
          citySlug = citySlug.substring('teambuilding-'.length)
        }

        if (!citySlug) return null

        // URL is always /teamevent/ regardless of locale
        return `/${localePrefix}/teamevent/${citySlug}/`
      }

      case EventListPageType.GENERAL: {
        const fullSlug = targetLocalization.slug || ''
        const slugParts = fullSlug.split('_')
        const slug = slugParts[0] || fullSlug
        return `/${localePrefix}/t/${slug}/`
      }

      default:
        return null
    }
  }

  const hasOtherLocales = (): boolean => {
    if (!eventListPageData) return false
    const localizations =
      strapiEventListPageGetters.getLocalizations(eventListPageData)
    if (!localizations?.length) return false
    const currentLocale = eventListPageData.locale?.toLowerCase()
    return localizations.some(
      (loc) => loc.locale?.toLowerCase() !== currentLocale
    )
  }

  return {
    buildLocaleUrl,
    hasOtherLocales,
  }
}
